import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import GlassCard from '@/effects/ui/GlassCard';
import { Camera, Users, User, Heart, TreePine, Building2, Waves, Star, Eye, Move, RotateCcw } from 'lucide-react';

const poseData = {
  standing: {
    label: '站姿',
    icon: User,
    color: '#7eccff',
    poses: [
      { name: '自然站立', desc: '双手自然下垂或插兜，身体微微侧转 15°', difficulty: 1, tag: '万能' },
      { name: '回眸一笑', desc: '侧身慢走，回头看向镜头，捕捉自然瞬间', difficulty: 2, tag: '惊艳' },
      { name: '仰拍大长腿', desc: '低角度仰拍，一脚前置延伸，身体后仰', difficulty: 2, tag: '显高' },
      { name: '靠墙大字', desc: '背靠墙面，四肢展开呈大字，放松随性', difficulty: 1, tag: '文艺' },
      { name: '背影杀', desc: '背对镜头，回头微侧脸，营造神秘氛围', difficulty: 2, tag: '故事感' },
      { name: '行走抓拍', desc: '自然向前行走，摄影师进行连拍抓拍', difficulty: 1, tag: '动态' },
    ]
  },
  sitting: {
    label: '坐姿',
    icon: User,
    color: '#a78bfa',
    poses: [
      { name: '侧坐搭腿', desc: '椅子侧坐，双腿交叠，手部自然搭放', difficulty: 1, tag: '优雅' },
      { name: '抱膝坐姿', desc: '席地而坐，双手抱膝，下巴微收', difficulty: 2, tag: '俏皮' },
      { name: '托腮看远', desc: '一手托腮，目光眺望远方，营造沉思感', difficulty: 1, tag: '文艺' },
      { name: '高脚椅放松', desc: '坐高脚椅，双脚踩横杆，身体前倾', difficulty: 2, tag: '休闲' },
      { name: '台阶坐姿', desc: '石阶/楼梯坐姿，一手撑地，双腿交叉', difficulty: 2, tag: '日常' },
    ]
  },
  lying: {
    label: '躺姿',
    icon: User,
    color: '#f472b6',
    poses: [
      { name: '草地仰望', desc: '平躺草地仰望天空，双手枕于脑后', difficulty: 2, tag: '治愈' },
      { name: '侧卧托头', desc: '侧身躺卧，一手托头，S 形曲线', difficulty: 3, tag: '唯美' },
      { name: '海滩沙雕', desc: '趴在沙滩上，双脚翘起，轻松惬意', difficulty: 2, tag: '度假' },
      { name: '酒店床照', desc: '侧躺酒店大床，枕头支撑，慵懒氛围', difficulty: 3, tag: 'ins风' },
    ]
  },
  couple: {
    label: '情侣',
    icon: Heart,
    color: '#fb7185',
    poses: [
      { name: '牵手背影', desc: '两人牵手向前走，自然不刻意', difficulty: 1, tag: '甜蜜' },
      { name: '公主抱', desc: '经典公主抱姿势，注意腰部用力', difficulty: 3, tag: '经典' },
      { name: '对望杀', desc: '两人深情对望，捕捉自然一瞬', difficulty: 2, tag: '浪漫' },
      { name: '比心合影', desc: '两人合力比心，贴近面部', difficulty: 1, tag: '趣味' },
      { name: '跳跃合影', desc: '两人同步跳跃，连拍捕捉最佳瞬间', difficulty: 3, tag: '活力' },
      { name: '亲吻侧颜', desc: '侧脸亲吻，自然不做作', difficulty: 2, tag: '甜蜜' },
    ]
  },
  group: {
    label: '多人',
    icon: Users,
    color: '#34d399',
    poses: [
      { name: '阶梯站位', desc: '高低错落站位，每人表情自然', difficulty: 2, tag: '团队' },
      { name: '跳跃抓拍', desc: '全员跳跃，注意脚步同步', difficulty: 3, tag: '活力' },
      { name: '背影杀', desc: '集体背对镜头望远方，手拉手', difficulty: 1, tag: '氛围' },
      { name: '框式构图', desc: '利用门窗框构图，人物自然填充', difficulty: 2, tag: '构图' },
      { name: '嬉戏抓拍', desc: '玩耍打闹瞬间，自然不做作', difficulty: 2, tag: '纪实' },
    ]
  },
  action: {
    label: '动态',
    icon: Move,
    color: '#fbbf24',
    poses: [
      { name: '奔跑抓拍', desc: '向前大步奔跑，连拍选择最佳姿态', difficulty: 3, tag: '活力' },
      { name: '旋转裙摆', desc: '旋转身体，裙摆飞扬，抓拍最佳角度', difficulty: 3, tag: '唯美' },
      { name: '跳跃触碰', desc: '伸手触碰高处物体（树叶/建筑）', difficulty: 2, tag: '创意' },
      { name: '吹泡泡/纸屑', desc: '配合道具制造动态，抓拍互动瞬间', difficulty: 2, tag: '趣味' },
      { name: '水边撩水', desc: '在海边/湖边撩水，抓拍水花', difficulty: 2, tag: '夏日' },
    ]
  },
};

const bgPoses = [
  { label: '花海', icon: TreePine, img: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=400&q=70' },
  { label: '城市', icon: Building2, img: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=400&q=70' },
  { label: '海边', icon: Waves, img: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&q=70' },
];

export default function Poses() {
  const [activeCategory, setActiveCategory] = useState('standing');
  const [activeBg, setActiveBg] = useState(null);
  const [bookmarked, setBookmarked] = useState({});

  const cat = poseData[activeCategory];
  const CatIcon = cat.icon;

  const toggleBookmark = (key) => setBookmarked(p => ({ ...p, [key]: !p[key] }));

  return (
    <>
      <Navbar />
      <div className="relative pt-24 pb-16 px-4 max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold cb mb-2 flex items-center gap-3">
            <Camera className="w-7 h-7" style={{ color: '#7eccff' }} />
            拍照姿势图鉴
          </h1>
          <p className="cd text-base">按体位分类 · 面向小白 · 不同场景 · 速成公式</p>
        </div>

        {/* 场景选择 */}
        <div className="flex gap-3 mb-6">
          <span className="text-sm cd self-center mr-2">场景：</span>
          {bgPoses.map(bg => {
            const Icon = bg.icon;
            const isActive = activeBg === bg.label;
            return (
              <button key={bg.label} onClick={() => setActiveBg(isActive ? null : bg.label)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition ${isActive ? 'text-white' : 'cd hover:!cb'}`}
                style={{ background: isActive ? 'rgba(106,179,214,0.3)' : 'rgba(106,179,214,0.08)', backdropFilter: 'blur(12px)' }}>
                <Icon className="w-3.5 h-3.5" /> {bg.label}
              </button>
            );
          })}
        </div>

        {/* 姿势分类切换 */}
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(poseData).map(([key, data]) => {
            const Icon = data.icon;
            return (
              <button key={key} onClick={() => setActiveCategory(key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm transition ${activeCategory === key ? 'text-white font-medium' : 'cd hover:!cb'}`}
                style={{ background: activeCategory === key ? data.color + '33' : 'rgba(106,179,214,0.06)', border: activeCategory === key ? `1px solid ${data.color}44` : '1px solid transparent' }}>
                <Icon className="w-4 h-4" /> {data.label}
              </button>
            );
          })}
        </div>

        {/* 当前分类头部 */}
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: cat.color + '22' }}>
            <CatIcon className="w-5 h-5" style={{ color: cat.color }} />
          </span>
          <div>
            <div className="text-base font-medium cb">{cat.label}指南</div>
            <div className="text-xs cd">{cat.poses.length} 个推荐姿势 · 难度从低到高</div>
          </div>
        </div>

        {/* 姿势列表 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cat.poses.map((pose, idx) => {
            const key = `${activeCategory}-${idx}`;
            const difficultyStars = '★'.repeat(pose.difficulty) + '☆'.repeat(3 - pose.difficulty);
            return (
              <GlassCard key={key} className="p-4 flex items-start gap-3" delay={idx * 0.05}>
                <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold shrink-0"
                  style={{ background: cat.color + '22', color: cat.color }}>{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium cb">{pose.name}</span>
                    <button onClick={() => toggleBookmark(key)} className="shrink-0">
                      <Star className={`w-4 h-4 ${bookmarked[key] ? '' : 'cd'}`} style={bookmarked[key] ? { color: '#ffd857' } : {}} />
                    </button>
                  </div>
                  <p className="text-xs cd leading-relaxed mb-2">{pose.desc}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: cat.color + '15', color: cat.color }}>{pose.tag}</span>
                    <span className="text-[10px] cd">{difficultyStars}</span>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>

        {/* 参考图卡片 */}
        {activeBg && (
          <div className="mt-8">
            <div className="text-sm font-medium cb mb-3">{activeBg}场景 - 参考构图</div>
            <div className="grid grid-cols-3 gap-3">
              {bgPoses.filter(b => b.label === activeBg).map(bg => (
                <div key={bg.label} className="relative h-48 rounded-xl overflow-hidden">
                  <img src={bg.img} alt={bg.label} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-3 left-3 text-white text-sm font-medium">{bg.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
